import type { ErklaerInhalt } from '../Erklaerung';

// Paket AA, Schritt AA-8 (Befund 6): reine Hilfen der Zwei-Faktor-Oberfläche, ohne React und ohne Anfrage.
// Die Antworten des Servers: GET /api/auth/two-factor (Stand), POST /setup (Einrichtung), backend/src/administration/twoFactor.
import { datum } from '../../utils/datum';

// Der Server sperrt ein Konto bis zur Einrichtung mit 403 und diesem Code (backend/src/middleware/auth.ts, AA-5).
export const ZWEI_FAKTOR_PFLICHT = 'TWO_FACTOR_SETUP_REQUIRED';

export type ZweiFaktorStand = { aktiv: boolean; seit: string | null; offeneCodes: number; pflicht: boolean; ohnePasswort: boolean };
export type ZweiFaktorEinrichtung = { schluessel: string; otpauthUri: string };

export function istPflichtFehler(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { code?: unknown } } } | null)?.response;
  return response?.status === 403 && response.data?.code === ZWEI_FAKTOR_PFLICHT;
}

// Vierergruppen mit Leerzeichen, damit man den Schlüssel von Hand abtippen kann.
export const gruppiereSchluessel = (schluessel: string) => schluessel.replace(/\s+/g, '').match(/.{1,4}/g)?.join(' ') ?? '';

// Sechs Ziffern aus der App; Leerzeichen (etwa „123 456“) zählen nicht.
export const codeGueltig = (code: string) => /^\d{6}$/.test(code.replace(/\s+/g, ''));

// Das Konto steht im Namen des Eintrags: otpauth://totp/<Aussteller>:<E-Mail>?… (backend/src/utils/totp.ts).
export function kontoAusUri(uri: string) {
  const treffer = /^otpauth:\/\/totp\/[^:?]*:([^?]*)/.exec(uri);
  try { return treffer ? decodeURIComponent(treffer[1]) : ''; } catch { return ''; }
}

const APP_HERKUNFT = 'Die App gibt es kostenlos im App Store oder bei Google Play.';
const EINSCHALT_FOLGE = 'Das Einschalten meldet Sie auf allen anderen Geräten ab.';

// Plan 1a Welle 1, G3-3 (D-12, MA-10; 8.06 bis 8.09 „… was eine ‚Authenticator-App‘ ist und was ich dafür brauche“, „Auf
// ‚Zwei-Faktor einrichten‘ klicke ich nicht, sonst stelle ich etwas um“): die Sätze an Knopf und Einrichtung an einer Stelle.
export const ZWEI_FAKTOR_TEXTE = {
  erklaerung: 'Schützt Ihr Konto mit einem zweiten Schritt bei der Anmeldung: einem sechsstelligen Code aus einer kostenlosen App am Handy '
    + '(Authenticator-App, etwa Microsoft Authenticator oder Google Authenticator). Eingeschaltet wird erst, wenn Sie einen Code aus der App '
    + 'bestätigen; vorher ändert sich nichts.',
  // Plan 1a Welle 2, UP-09 (SPEC-USER.md UE-01): Schritt 2 sagt, welcher Weg für welches Gerät gilt. Am Computer öffnet der
  // Knopf „In der Authenticator-App öffnen“ nichts, weil dort keine solche App ist; ohne QR-Code (UF-5, AA-F2).
  // Spur nachbesserung-m3, G-2 (MESSUNG-M3.md 6.4, B-ludwig P-7 „„Authenticator-App“ ist für mich ein Fremdwort“; NA-17, G-09):
  // Schritt 1 sagt zuerst, was die App tut, dann ihr Fachwort; der letzte Teil bleibt als Zitat der Hilfe gültig.
  schritte: [
    'Installieren Sie am Handy eine kostenlose App, die alle 30 Sekunden einen neuen sechsstelligen Code zeigt. Solche Apps heißen Authenticator-App, etwa Microsoft Authenticator oder Google Authenticator (App Store oder Google Play).',
    'Fügen Sie dort ein Konto hinzu. Am Handy tippen Sie auf „In der Authenticator-App öffnen“. Am Computer tippen Sie den Schlüssel in der App ab. Der Schlüssel verbindet die App mit Ihrem Konto.',
    'Geben Sie den sechsstelligen Code aus der App ein und wählen Sie „Bestätigen“. Erst dann ist die Zwei-Faktor-Anmeldung eingeschaltet.',
  ],
  // Plan 1a Welle 2, Spur admin (SPEC-ADMIN.md 5.1, Stück 3, P3-03): die zweite Fassung von Schritt 2. Sie steht nur, wenn der
  // Dialog das Bild zum Abfotografieren zeigt (ab 800 px Fensterbreite und nur, solange der eine Wert in qrBild.ts es zulässt);
  // sonst bleibt die Fassung darüber Zeichen für Zeichen.
  schritt2MitBild: 'Öffnen Sie die App am Handy, wählen Sie „Konto hinzufügen“ und halten Sie die Kamera auf das Bild. '
    + 'Ohne Kamera tippen Sie den Schlüssel darunter ab. Bild und Schlüssel verbinden die App mit Ihrem Konto.',
  // UE-01: über den Schritten, nur im Dialog (dort gibt es „Abbrechen“); die Hilfe sagt denselben Satz.
  vorDemCode: 'Bis Sie einen Code bestätigen, ändert sich nichts.',
  // F-24 (PF-TX-rahmen-4-nach, GRUNDREGELN 2 „immer sichtbar“): im Dialog direkt nach vorDemCode, im selben Kasten; der Server
  // beendet beim Bestätigen alle anderen Anmeldungen des Kontos (backend twoFactor/service.ts).
  einschaltFolge: EINSCHALT_FOLGE,
  // G-2 (B-ludwig P-7 „eine lange Buchstabenreihe steht da“): die Zeile über dem Schlüssel in Schritt 2.
  schluesselBeschriftung: 'Schlüssel zum Abtippen in der App:',
  // UP-09: eigene Zeile unter den zwei Knöpfen von Schritt 2. Der Satz nennt den Knopf, den er meint, und steht nicht im Link.
  nurAmHandy: '„In der Authenticator-App öffnen“ funktioniert nur am Handy.',
  // UQ-06: erscheint nach einem Klick oder Tipp auf den Link und bleibt stehen, damit der Klick nie ohne sichtbare Folge ist.
  keineApp: 'Hat sich keine App geöffnet? Dann tippen Sie den Schlüssel in der Authenticator-App am Handy ab.',
  codesSpeichern: 'Codes als Datei speichern',
  codesSatz: 'Bewahren Sie die Codes getrennt vom Handy auf, etwa ausgedruckt. Mit einem Code melden Sie sich an, wenn das Handy fehlt. Sie sehen sie nur jetzt.',
  // D-15 (M1-36 „Hier geht's nur um mein Konto.“): der Weg von „Sicherheit“ zur Regel für alle in der Firma.
  firmaVerweis: (firma: string) => `Zwei-Faktor für alle in ${firma} verlangen: „Sicherheit der Firma“`,
  firmaAnker: 'sicherheit-der-firma',
  // Nachbesserung I/Z-04 (VORAB-MESSUNG 7.2 Nr 2c; welle-2/nachbesserung/AUFTEILUNG.md A-15, W-16): unter dem Erklärsatz, solange
  // die Zwei-Faktor-Anmeldung nicht eingerichtet ist und das Konto ein eigenes Passwort hat – woher die App kommt (wie Schritt 1
  // der Hilfe), was das Einschalten bewirkt (Hinweis der Hilfe wörtlich) und der Weg zur Anleitung (Titel des Hilfe-Themas).
  // F-24 (PF-TX-rahmen-4, SPEC-rahmen 3.3): sichtbar nur noch eine Zeile; das Übrige im Fenster ZWEI_FAKTOR_FENSTER, dessen
  // „Mehr in der Hilfe“ das Thema hinter anleitung öffnet.
  kurz: 'Zweiter Schritt bei der Anmeldung: ein Code aus einer App am Handy.',
  appHinweis: `${APP_HERKUNFT} ${EINSCHALT_FOLGE}`,
  anleitung: 'Anleitung: Zwei-Faktor-Anmeldung einrichten',
} as const;

/** F-24 (PF-TX-rahmen-4, SPEC-rahmen 3.3 F3): die Zwei-Faktor-Anmeldung in drei Schritten; Teil 2 endet wie erklaerung. */
export const ZWEI_FAKTOR_FENSTER: ErklaerInhalt = {
  frage: 'Wie funktioniert die Zwei-Faktor-Anmeldung?',
  teile: [
    { titel: '1. App holen', text: `Laden Sie eine Authenticator-App aufs Handy, etwa Microsoft Authenticator oder Google Authenticator. ${APP_HERKUNFT}` },
    { titel: '2. Einrichten', text: 'Klicken Sie „Zwei-Faktor einrichten“. Eingeschaltet wird erst, wenn Sie einen Code aus der App bestätigen; vorher ändert sich nichts.' },
    { titel: '3. Anmelden', text: 'Bei der Anmeldung folgt auf das Passwort ein zweiter Schritt: der sechsstellige Code aus der App.' },
  ],
  fuss: EINSCHALT_FOLGE,
  hilfe: 'zwei-faktor',
};

// Inhalt der Textdatei mit den Wiederherstellungscodes; produkt ist der Produktname der Marke (MK-1), nie der Firmenname.
export function codesAlsText(codes: string[], { produkt, email, datum: erstellt }: { produkt: string; email: string; datum: Date }) {
  return [
    email ? `${produkt} – Wiederherstellungscodes für ${email}` : `${produkt} – Wiederherstellungscodes`,
    `erstellt am ${datum(erstellt)}`,
    '',
    ...codes,
    '',
    'Jeder Code gilt einmal. Bewahren Sie die Datei getrennt von Ihrem Gerät auf.',
    '',
  ].join('\n');
}
